import React, { useEffect, useRef, useState } from 'react';

export interface SelectionMenuProps {
  /** N: filter-visible printings on the page. */
  totalCount: number;
  /** M: how many of those are selected. */
  selectedCount: number;
  onSelectAll: () => void;
  onRemoveAll: () => void;
  onInvert: () => void;
}

const WANT_NO_ICON =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg';

/**
 * The page-level selection control: a 34px field reading "M selected" that
 * opens the three whole-page actions.
 *
 * It lives in the existing title row rather than adding one, because the header
 * has to come in UNDER its old height while gaining this control.
 */
const SelectionMenu: React.FC<SelectionMenuProps> = ({
  totalCount,
  selectedCount,
  onSelectAll,
  onRemoveAll,
  onInvert,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside mousedown and on Escape, matching SetFilter so both
  // dropdowns in this header dismiss the same way.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const run = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <div
      className="cp-selection"
      ref={menuRef}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setIsOpen(false);
      }}
    >
      <button
        type="button"
        className={`cp-selection-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span className="cp-selection-count">{selectedCount}</span>
        <span className="cp-selection-label">selected</span>
        <svg className={`cp-chevron ${isOpen ? 'rotated' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="cp-selection-menu" role="menu">
          <button type="button" className="cp-selection-item" role="menuitem" onClick={() => run(onSelectAll)}>
            Select all {totalCount}
          </button>
          <button type="button" className="cp-selection-item" role="menuitem" onClick={() => run(onRemoveAll)}>
            <img src={WANT_NO_ICON} alt="" aria-hidden="true" width={16} height={16} />
            Remove all {totalCount}
          </button>
          <button type="button" className="cp-selection-item" role="menuitem" onClick={() => run(onInvert)}>
            Invert selection
          </button>
        </div>
      )}
    </div>
  );
};

export default SelectionMenu;
